// Google Ads API Integration
// This file provides functions to fetch Google Ads data
// You'll need to set up Google Ads API credentials and replace the mock data



interface CampaignData {
  name: string;
  spend: number;
  clicks: number;
  impressions: number;
  conversions: number;
}

interface GoogleAdsResponse {
  totalSpend: number;
  totalClicks: number;
  totalImpressions: number;
  ctr: number;
  cpc: number;
  roas: number;
  campaigns: CampaignData[];
  keywords?: KeywordData[];
  demographics?: DemographicData;
  geographic?: GeographicData[];
  devices?: DeviceData[];
  searchTerms?: SearchTermData[];
}

interface KeywordData {
  keyword: string;
  matchType: string;
  clicks: number;
  impressions: number;
  spend: number;
  conversions: number;
  ctr: number;
  cpc: number;
  qualityScore?: number;
}

interface DemographicData {
  ageRanges: { range: string; clicks: number; conversions: number }[];
  genders: { gender: string; clicks: number; conversions: number }[];
  incomeRanges: { range: string; clicks: number; conversions: number }[];
}

interface GeographicData {
  location: string;
  clicks: number;
  impressions: number;
  spend: number;
  conversions: number;
}

interface DeviceData {
  device: string;
  clicks: number;
  impressions: number;
  spend: number;
  conversions: number;
  ctr: number;
}

interface SearchTermData {
  searchTerm: string;
  clicks: number;
  impressions: number;
  spend: number;
  conversions: number;
  addedAsKeyword: boolean;
}

// Frontend no longer needs Google Ads credentials - they're handled by serverless functions

// Proxy base URL (configurable). If empty, use same-origin relative path for Vercel/serverless setups.
const PROXY_BASE_URL = (process.env.REACT_APP_GOOGLE_ADS_PROXY_URL || '').replace(/\/+$/, '');
const PROXY_SEARCH_PATH = '/api/google-ads/search';




/**
 * Fetch Google Ads performance data
 */
export async function fetchGoogleAdsData(dateRange: string = '7'): Promise<GoogleAdsResponse> {
  try {
    console.log('🔍 Fetching Google Ads data via proxy...');

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - parseInt(dateRange));

    const startDateStr = startDate.toISOString().split('T')[0].replace(/-/g, '');
    const endDateStr = endDate.toISOString().split('T')[0].replace(/-/g, '');

    // Use proxy server to avoid CORS issues
    const query = `
      SELECT
        campaign.name,
        metrics.cost_micros,
        metrics.clicks,
        metrics.impressions,
        metrics.conversions
      FROM campaign
      WHERE segments.date BETWEEN '${startDateStr}' AND '${endDateStr}'
      AND campaign.status = 'ENABLED'
    `;

    console.log('🔄 Making request to proxy server...');

    const url = PROXY_BASE_URL ? `${PROXY_BASE_URL}${PROXY_SEARCH_PATH}` : PROXY_SEARCH_PATH;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: query,
        dateRange: dateRange
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Proxy server error:', errorData);
      throw new Error(`Proxy server error: ${errorData.error}`);
    }

    const data = await response.json();
    console.log('✅ Received data from proxy server:', data);

    // Process the response data
    return processGoogleAdsResponse(data);

  } catch (error) {
    console.error('Error fetching Google Ads data:', error);
    // Fallback to mock data if API fails
    return getMockGoogleAdsData();
  }
}

/**
 * Process Google Ads API response
 */
function processGoogleAdsResponse(apiResponse: any): GoogleAdsResponse {
  // TODO: Process actual API response
  // This is a placeholder - you'll need to adapt based on the actual API response structure

  const campaigns: CampaignData[] = [];
  let totalSpend = 0;
  let totalClicks = 0;
  let totalImpressions = 0;
  let totalConversions = 0;

  // Process each result from the API
  if (apiResponse.results) {
    apiResponse.results.forEach((result: any) => {
      const spend = result.metrics.costMicros / 1000000; // Convert from micros
      const clicks = result.metrics.clicks;
      const impressions = result.metrics.impressions;
      const conversions = result.metrics.conversions;

      campaigns.push({
        name: result.campaign.name,
        spend,
        clicks,
        impressions,
        conversions,
      });

      totalSpend += spend;
      totalClicks += clicks;
      totalImpressions += impressions;
      totalConversions += conversions;
    });
  }

  const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const cpc = totalClicks > 0 ? totalSpend / totalClicks : 0;

  // Calculate ROAS (you'll need to connect this to your actual revenue data)
  const estimatedRevenue = totalConversions * 8000; // Assuming $8k average quote value
  const roas = totalSpend > 0 ? estimatedRevenue / totalSpend : 0;

  return {
    totalSpend,
    totalClicks,
    totalImpressions,
    ctr,
    cpc,
    roas,
    campaigns,
  };
}

/**
 * Mock data for development/testing
 */
function getMockGoogleAdsData(): GoogleAdsResponse {
  return {
    totalSpend: 1250.75,
    totalClicks: 342,
    totalImpressions: 15420,
    ctr: 2.22,
    cpc: 3.66,
    roas: 4.2,
    campaigns: [
      {
        name: "Pergolas - Toronto",
        spend: 650.25,
        clicks: 178,
        impressions: 8200,
        conversions: 12
      },
      {
        name: "Awnings - GTA",
        spend: 425.50,
        clicks: 115,
        impressions: 5100,
        conversions: 8
      },
      {
        name: "Outdoor Living - Broad",
        spend: 175.00,
        clicks: 49,
        impressions: 2120,
        conversions: 3
      }
    ],
    keywords: [
      {
        keyword: "pergola toronto",
        matchType: "Exact",
        clicks: 89,
        impressions: 1245,
        spend: 325.50,
        conversions: 7,
        ctr: 7.15,
        cpc: 3.66,
        qualityScore: 8
      },
      {
        keyword: "custom pergola",
        matchType: "Phrase",
        clicks: 67,
        impressions: 2134,
        spend: 245.75,
        conversions: 4,
        ctr: 3.14,
        cpc: 3.67,
        qualityScore: 7
      },
      {
        keyword: "retractable awning",
        matchType: "Exact",
        clicks: 45,
        impressions: 987,
        spend: 189.25,
        conversions: 5,
        ctr: 4.56,
        cpc: 4.21,
        qualityScore: 9
      }
    ],
    demographics: {
      ageRanges: [
        { range: "25-34", clicks: 89, conversions: 6 },
        { range: "35-44", clicks: 134, conversions: 12 },
        { range: "45-54", clicks: 87, conversions: 4 },
        { range: "55-64", clicks: 32, conversions: 1 }
      ],
      genders: [
        { gender: "Male", clicks: 198, conversions: 14 },
        { gender: "Female", clicks: 144, conversions: 9 }
      ],
      incomeRanges: [
        { range: "Top 10%", clicks: 67, conversions: 8 },
        { range: "11-20%", clicks: 89, conversions: 7 },
        { range: "21-30%", clicks: 78, conversions: 5 },
        { range: "Lower 70%", clicks: 108, conversions: 3 }
      ]
    },
    geographic: [
      { location: "Toronto, ON", clicks: 156, impressions: 2345, spend: 567.25, conversions: 12 },
      { location: "Mississauga, ON", clicks: 89, impressions: 1456, spend: 324.50, conversions: 6 },
      { location: "Brampton, ON", clicks: 67, impressions: 1123, spend: 245.75, conversions: 3 },
      { location: "Markham, ON", clicks: 30, impressions: 567, spend: 113.25, conversions: 2 }
    ],
    devices: [
      { device: "Desktop", clicks: 189, impressions: 6789, spend: 689.25, conversions: 15, ctr: 2.78 },
      { device: "Mobile", clicks: 123, impressions: 5634, spend: 445.50, conversions: 6, ctr: 2.18 },
      { device: "Tablet", clicks: 30, impressions: 2997, spend: 116.00, conversions: 2, ctr: 1.00 }
    ],
    searchTerms: [
      { searchTerm: "pergola installation toronto", clicks: 45, impressions: 234, spend: 165.75, conversions: 4, addedAsKeyword: true },
      { searchTerm: "custom outdoor pergola", clicks: 34, impressions: 189, spend: 124.50, conversions: 3, addedAsKeyword: false },
      { searchTerm: "retractable awning price", clicks: 28, impressions: 156, spend: 98.25, conversions: 2, addedAsKeyword: true },
      { searchTerm: "backyard shade solutions", clicks: 23, impressions: 134, spend: 67.50, conversions: 1, addedAsKeyword: false }
    ]
  };
}

/**
 * Setup instructions for Google Ads API
 */
export const SETUP_INSTRUCTIONS = `
To connect real Google Ads data:

1. Go to Google Ads API Console: https://developers.google.com/google-ads/api
2. Create a new project or select existing one
3. Enable Google Ads API
4. Create OAuth 2.0 credentials
5. Get your Developer Token from Google Ads account
6. Add these environment variables to your .env file:

REACT_APP_GOOGLE_ADS_CLIENT_ID=your_client_id
REACT_APP_GOOGLE_ADS_CLIENT_SECRET=your_client_secret
REACT_APP_GOOGLE_ADS_REFRESH_TOKEN=your_refresh_token
REACT_APP_GOOGLE_ADS_CUSTOMER_ID=your_customer_id
REACT_APP_GOOGLE_ADS_DEVELOPER_TOKEN=your_developer_token

7. Replace the mock data in fetchGoogleAdsData() with actual API calls
8. Test with a small date range first
`;
